import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

/**
 * Guard de autorização das rotas administrativas do TENANT
 * (`/:tenantId/configuracoes/**`).
 *
 * Complementa `authGuard` + `tenantGuard`: além de autenticado e com tenant
 * válido, o usuário precisa ser **administrador do tenant** (`isTenantAdmin`).
 *
 * Fail-closed: sem papel administrativo → acesso negado. O papel vem do JWT
 * decodificado (síncrono, sobrevive a F5); em acesso negado, redireciona para
 * o dashboard do tenant (comportamento seguro, sem quebrar deep links válidos).
 *
 * Não substitui a autorização do backend — é a camada de rota do frontend.
 */
export const tenantAdminGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const tenantId = route.pathFromRoot
    .map(r => r.paramMap.get('tenantId'))
    .find((id): id is string => !!id) ?? null;

  const deny = () =>
    tenantId
      ? router.createUrlTree(['/', tenantId, 'dashboard'])
      : router.createUrlTree(['/auth/login']);

  if (auth.isTenantAdmin()) return true;

  // Usuário ainda não hidratado (ex.: F5): as roles já vêm do JWT, mas
  // garantimos o carregamento do usuário antes de negar.
  if (!auth.user()) {
    return auth.loadCurrentUser().pipe(
      map(() => (auth.isTenantAdmin() ? true : deny())),
    );
  }

  return deny();
};
